import React, { useCallback, useState } from "react";

import { Spacer } from "shared/layout";
import { IFileType } from "shared/types";
import { FileUpload, FileUploadProps } from "./FileUpload";
import { ImagePreview } from "./styles";

export enum IImagePreviewPreset {
  default = "default",
  logo = "logo",
  cover = "cover",
}

export const imagePreviewPresets = {
  [IImagePreviewPreset.default]: {
    width: "auto",
    height: "200px",
  },
  [IImagePreviewPreset.logo]: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
  },
  [IImagePreviewPreset.cover]: {
    width: "100%",
    height: "300px",
  },
};

interface ImageUploadProps extends FileUploadProps {
  preset?: IImagePreviewPreset;
}

const noRenderPreviewTypes = [".heic", ".heif"];

const acceptedTypes = [...noRenderPreviewTypes, ".jpg", ".jpeg", ".png", ".webp"];

export const ImageUpload: React.FC<ImageUploadProps> = ({
  preset = IImagePreviewPreset.default,
  error,
  value,
  ...rest
}) => {
  // (!) don't revoke image URL, since we need it in the preview
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleClear = () => {
    setSelectedFile(null);
  };

  const renderPreview = useCallback(() => {
    if (!value) return;

    const noRenderPreviewMIMETypes = [IFileType.heic, IFileType.heif];

    if (
      (selectedFile?.type && noRenderPreviewMIMETypes.includes(selectedFile?.type)) ||
      noRenderPreviewTypes.some(type => value.originalURL && value?.originalURL.endsWith(type))
    ) {
      // Don't render preview image.
      return;
    }

    if (!value?.file && value?.originalURL && !selectedFile) {
      return (
        <ImagePreview style={imagePreviewPresets[preset]}>
          <img src={value.originalURL} alt="" style={imagePreviewPresets[preset]} />
        </ImagePreview>
      );
    }

    if (!error && selectedFile && URL.createObjectURL(selectedFile)) {
      return (
        <ImagePreview style={imagePreviewPresets[preset]}>
          <img
            src={URL.createObjectURL(selectedFile!)}
            alt=""
            style={imagePreviewPresets[preset]}
          />
        </ImagePreview>
      );
    }
  }, [error, preset, selectedFile, value]);

  return (
    <Spacer size="s">
      <FileUpload
        {...rest}
        value={value}
        accept={acceptedTypes.join(",")}
        error={error}
        handleFileChange={(file: File) => setSelectedFile(file)}
        handleFileClear={handleClear}
      />
      {renderPreview()}
    </Spacer>
  );
};
